import apiClient from './apiClient';
import { FILE_ENDPOINTS } from '../config/api';

class FileService {
  /**
   * Upload a file to the server
   * @param {File} file - The file object to upload
   * @param {function} onProgress - Progress callback function (optional)
   * @returns {Promise<Object>} Upload response with file details
   */
  async uploadFile(file, onProgress = null) {
    try {
      // Validate file input
      if (!file || !(file instanceof File)) {
        throw new Error('Invalid file provided. Please provide a valid File object.');
      }

      // Get token from localStorage
      const authToken = localStorage.getItem('access_token');
      if (!authToken) {
        throw new Error('No authorization token found. Please login first.');
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      // Use the production API endpoint directly for file uploads
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://prod.backend.organisewise.me';
      // Remove /api suffix if present for file uploads
      const fileApiBaseUrl = apiBaseUrl.replace('/api', '');
      const uploadEndpoint = `${fileApiBaseUrl}/files/upload`;

      // For file uploads, we need to use fetch directly to handle FormData properly
      // and avoid setting Content-Type header (let browser set it with boundary)
      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || errorData.error || 'Upload failed';
        
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        } else if (response.status === 413) {
          throw new Error('File too large. Please choose a smaller file.');
        } else if (response.status === 415) {
          throw new Error('File type not supported.');
        } else {
          throw new Error(`Upload failed: ${message}`);
        }
      }

      const data = await response.json();

      // Validate response
      if (!data || !data.success) {
        throw new Error('Upload failed: Invalid response from server');
      }

      return {
        success: true,
        data: {
          fileName: data.file_name,
          uniqueUrl: data.unique_url,
          fileSize: data.file_size,
          uploadedAt: new Date().toISOString(),
        },
        message: 'File uploaded successfully'
      };

    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  }

  /**
   * Get file information by URL or file ID
   * @param {string} fileIdentifier - File URL or ID
   * @returns {Promise<Object>} File information
   */
  async getFile(fileIdentifier) {
    try {
      // Validate input
      if (!fileIdentifier || typeof fileIdentifier !== 'string') {
        throw new Error('Invalid file identifier provided.');
      }

      // Determine if it's a URL or ID
      let endpoint;
      if (fileIdentifier.startsWith('http')) {
        // Extract file ID from URL if it's a full URL
        const urlParts = fileIdentifier.split('/');
        const fileId = urlParts[urlParts.length - 1];
        endpoint = FILE_ENDPOINTS.FILE_BY_ID(fileId);
      } else {
        // Assume it's a file ID
        endpoint = FILE_ENDPOINTS.FILE_BY_ID(fileIdentifier);
      }

      const data = await apiClient.get(endpoint);

      return {
        success: true,
        data: data,
        message: 'File information retrieved successfully'
      };

    } catch (error) {
      console.error('Get file error:', error);
      throw error;
    }
  }

  /**
   * Fetch a protected asset and return as blob URL
   * @param {string} assetPath - Asset path (e.g., "7/lgXax4lDvV")
   * @param {string} fileName - Optional filename for the request
   * @returns {Promise<string>} Blob URL for the asset
   */
  async fetchProtectedAsset(assetPath, fileName = null) {
    try {
      // Validate input
      if (!assetPath || typeof assetPath !== 'string') {
        throw new Error('Invalid asset path provided.');
      }

      // Get token from localStorage
      const authToken = localStorage.getItem('access_token');
      if (!authToken) {
        throw new Error('No authorization token found. Please login first.');
      }

      // Prepare the API endpoint
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
      // Remove /api from the base URL for the file endpoint
      const fileApiBaseUrl = apiBaseUrl.replace('/api', '');
      const endpoint = `${fileApiBaseUrl}/files/assets/${assetPath}`;

      // Prepare request body
      const requestBody = fileName ? { file_name: fileName } : {};

      // Make the request
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: Object.keys(requestBody).length > 0 ? JSON.stringify(requestBody) : null,
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        } else if (response.status === 404) {
          throw new Error('Asset not found.');
        } else {
          throw new Error(`Failed to fetch asset: ${response.status}`);
        }
      }

      // Get the blob data
      const blob = await response.blob();
      
      // Create and return blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      return blobUrl;

    } catch (error) {
      console.error('Fetch protected asset error:', error);
      throw error;
    }
  }

  /**
   * Download a file from the server
   * @param {string} fileUrl - The file URL to download
   * @param {string} fileName - Optional custom filename for download
   * @returns {Promise<Object>} Download result
   */
  async downloadFile(fileUrl, fileName = null) {
    try {
      // Validate input
      if (!fileUrl || typeof fileUrl !== 'string') {
        throw new Error('Invalid file URL provided.');
      }

      // Get token from localStorage
      const authToken = localStorage.getItem('access_token');
      if (!authToken) {
        throw new Error('No authorization token found. Please login first.');
      }

      // Make the request using fetch for blob response
      const response = await fetch(fileUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || errorData.error || 'Download failed';
        
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        } else if (response.status === 404) {
          throw new Error('File not found or no longer available.');
        } else {
          throw new Error(`Download failed: ${message}`);
        }
      }

      // Get the blob data
      const blob = await response.blob();

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename
      if (fileName) {
        link.download = fileName;
      } else {
        // Try to extract filename from URL or Content-Disposition header
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch) {
            link.download = fileNameMatch[1];
          }
        } else {
          // Extract from URL
          const urlParts = fileUrl.split('/');
          link.download = urlParts[urlParts.length - 1] || 'download';
        }
      }
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        message: 'File downloaded successfully'
      };

    } catch (error) {
      console.error('Download file error:', error);
      throw error;
    }
  }

  /**
   * Delete a file from the server
   * @param {string} fileIdentifier - File URL or ID
   * @returns {Promise<Object>} Delete result
   */
  async deleteFile(fileIdentifier) {
    try {
      // Validate input
      if (!fileIdentifier || typeof fileIdentifier !== 'string') {
        throw new Error('Invalid file identifier provided.');
      }

      // Determine if it's a URL or ID
      let endpoint;
      if (fileIdentifier.startsWith('http')) {
        // Extract file ID from URL if it's a full URL
        const urlParts = fileIdentifier.split('/');
        const fileId = urlParts[urlParts.length - 1];
        endpoint = FILE_ENDPOINTS.DELETE_FILE(fileId);
      } else {
        // Assume it's a file ID
        endpoint = FILE_ENDPOINTS.DELETE_FILE(fileIdentifier);
      }

      await apiClient.delete(endpoint);

      return {
        success: true,
        message: 'File deleted successfully'
      };

    } catch (error) {
      console.error('Delete file error:', error);
      throw error;
    }
  }

  /**
   * Get list of uploaded files
   * @param {Object} params - Query parameters (page, limit, etc.)
   * @returns {Promise<Object>} List of files with pagination
   */
  async getFiles(params = {}) {
    try {
      const data = await apiClient.get(FILE_ENDPOINTS.FILES, params);

      return {
        success: true,
        data: {
          files: data.files || [],
          pagination: data.pagination || {
            current_page: 1,
            total_pages: 1,
            total_items: data.files?.length || 0,
            limit: data.files?.length || 0,
            has_next_page: false,
            has_previous_page: false
          }
        },
        message: 'Files retrieved successfully'
      };

    } catch (error) {
      console.error('Get files error:', error);
      throw error;
    }
  }

  /**
   * Validate file before upload
   * @param {File} file - The file to validate
   * @param {Object} options - Validation options
   * @returns {Object} Validation result
   */
  validateFile(file, options = {}) {
    const {
      maxSize = 10 * 1024 * 1024, // 10MB default
      allowedTypes = [
        'image/jpeg',
        'image/jpg', 
        'image/png',
        'image/gif',
        'image/webp',
        'application/pdf',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ]
    } = options;

    const errors = [];

    // Check if file exists
    if (!file || !(file instanceof File)) {
      errors.push('No file provided or invalid file object');
      return { isValid: false, errors };
    }

    // Check file size
    if (file.size > maxSize) {
      errors.push(`File size (${this.formatFileSize(file.size)}) exceeds maximum allowed size (${this.formatFileSize(maxSize)})`);
    }

    // Check file type
    if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
      errors.push(`File type "${file.type}" is not allowed. Allowed types: ${allowedTypes.join(', ')}`);
    }

    // Check file name
    if (!file.name || file.name.trim() === '') {
      errors.push('File must have a valid name');
    }

    return {
      isValid: errors.length === 0,
      errors,
      fileInfo: {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      }
    };
  }

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

// Create and export a singleton instance
const fileServiceInstance = new FileService();
export default fileServiceInstance; 